import { useProjectParams } from '@hooks/useProjectParams';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { WorkOrderStructurePageLayout } from 'pages/Project/Common/WorkOrderStructure/WorkOrderStructurePageLayout';
import { methodologyMapReverse } from 'types/methodology';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import type React from 'react';
import { useMemo } from 'react';

import { UerWorkOrderObjectEditComponent } from './components/UerWorkOrderObjectEditComponent';
import { useGetUerWorkOrderStructureDataSource } from './hooks/useGetUerWorkOrderStructureDataSource';
import { useUerRemoveWorkOrderObject } from './hooks/useUerRemoveWorkOrderObject';
import { useUerWorkOrderEditObjectDataSource } from './hooks/useUerWorkOrderEditObjectDataSource';
import { useUerWorkOrderGetIncludedCountData } from './hooks/useUerWorkOrderGetIncludedCountData';

export const UerWorkOrderStructure: React.FC = () => {
    const { id: projectId, methodology, orderId } = useProjectParams();
    const { projectData } = useProjectData(projectId);
    const { order: orderRules } = useAccess();
    const workOrderId = useMemo(() => (orderId ? Number(orderId) : undefined), [orderId]);

    const currentWorkOrder = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((nav) => nav.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const workOrderFullNumber = useMemo(() => getLabelWorkOrder(currentWorkOrder, true), [currentWorkOrder]);

    const isTopFilterReadOnly = useMemo(
        () => currentWorkOrder?.status !== WorkOrderStatusEnum.OnFormation,
        [currentWorkOrder?.status],
    );

    const isReadOnly = useMemo(
        () =>
            [WorkOrderStatusEnum.Completed, WorkOrderStatusEnum.Delayed].includes(
                currentWorkOrder?.status as WorkOrderStatusEnum,
            ),
        [currentWorkOrder?.status],
    );

    return (
        <WorkOrderStructurePageLayout
            isFrameContract
            workOrderOrChangeId={workOrderId}
            methodology={methodologyMapReverse[methodology as string]}
            projectData={projectData}
            useGetDataSource={useGetUerWorkOrderStructureDataSource}
            WorkOrderObjectEditComponent={UerWorkOrderObjectEditComponent}
            WorkOrderSubObjectEditComponent={UerWorkOrderObjectEditComponent}
            workOrderFullNumber={workOrderFullNumber}
            useWorkOrderEditObjectDataSource={useUerWorkOrderEditObjectDataSource}
            useRemoveWorkOrderObject={useUerRemoveWorkOrderObject}
            useGetIncludedCountData={useUerWorkOrderGetIncludedCountData}
            isTopFilterReadOnly={isTopFilterReadOnly}
            status={currentWorkOrder?.status}
            canEditObjectCode={
                orderRules.canEditObjectCode && currentWorkOrder?.status === WorkOrderStatusEnum.OnFormation
            }
            isReadonly={isReadOnly}
        />
    );
};

UerWorkOrderStructure.displayName = 'UerWorkOrderStructure';
